import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button, Dropdown, Header, Icon, Image, Label, Loader, Message, Segment, Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import TournamentMatchModal from './TournamentMatchModal';
import { TournamentDto, TournamentMatchDto, TournamentRegistrationDto, TournamentStandingRow } from '../../app/models/Tournament';

// Returns the background color for a form result badge
const formColor = (r: string) => {
    if (r === 'W') return '#21ba45';
    if (r === 'L') return '#db2828';
    return '#fbbd08';
};

interface StandingsTableProps {
    rows: TournamentStandingRow[];
    highlightAdvancing?: boolean;
    advancingCount?: number;
    title?: string;
}

// Standings table used for both Round Robin and Group Stage views
export function StandingsTable({ rows, highlightAdvancing, advancingCount, title }: StandingsTableProps) {
    const { t } = useTranslation();

    return (
        <div style={{ marginBottom: 20 }}>
            {title && <Header as="h4" color="teal" style={{ marginBottom: 8 }}>{title}</Header>}
            <div style={{ overflowX: 'auto' }}>
                <Table celled compact striped unstackable size="small">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={1}>#</Table.HeaderCell>
                            <Table.HeaderCell>{t('tournament.standings.team')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">{t('tournament.standings.played')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center" style={{ color: '#21ba45' }}>{t('tournament.standings.wins')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center" style={{ color: '#fbbd08' }}>{t('tournament.standings.draws')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center" style={{ color: '#db2828' }}>{t('tournament.standings.losses')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">{t('tournament.standings.gf')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">{t('tournament.standings.ga')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">{t('tournament.standings.gd')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center" style={{ fontWeight: 900 }}>{t('tournament.standings.pts')}</Table.HeaderCell>
                            <Table.HeaderCell textAlign="center">{t('tournament.standings.form')}</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map((row, i) => {
                            const isAdvancing = !!(highlightAdvancing && advancingCount && i < advancingCount);
                            return (
                                <Table.Row
                                    key={row.teamId}
                                    positive={isAdvancing}
                                    style={isAdvancing ? { background: '#f0fff4' } : undefined}
                                >
                                    <Table.Cell>
                                        {isAdvancing && <Icon name="arrow up" color="green" size="small" />}
                                        {row.rank}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Image
                                                src={row.teamLogoUrl || '/assets/team-placeholder.png'}
                                                avatar style={{ width: 24, height: 24 }}
                                            />
                                            <Link to={`/teams/${row.teamId}`}>{row.teamName}</Link>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">{row.played}</Table.Cell>
                                    <Table.Cell textAlign="center" style={{ fontWeight: 700, color: '#21ba45' }}>{row.wins}</Table.Cell>
                                    <Table.Cell textAlign="center" style={{ color: '#e0a800' }}>{row.draws}</Table.Cell>
                                    <Table.Cell textAlign="center" style={{ color: '#db2828' }}>{row.losses}</Table.Cell>
                                    <Table.Cell textAlign="center">{row.goalsFor}</Table.Cell>
                                    <Table.Cell textAlign="center">{row.goalsAgainst}</Table.Cell>
                                    <Table.Cell textAlign="center" style={{ color: row.goalDiff >= 0 ? '#21ba45' : '#db2828' }}>
                                        {row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}
                                    </Table.Cell>
                                    <Table.Cell textAlign="center" style={{ fontWeight: 900, fontSize: '1.1em' }}>{row.points}</Table.Cell>
                                    <Table.Cell>
                                        <span style={{ display: 'inline-flex', gap: 3 }}>
                                            {row.form.map((r, fi) => (
                                                <span key={fi} style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 20, height: 20, borderRadius: '50%',
                                                    background: formColor(r), color: '#fff',
                                                    fontSize: '0.7em', fontWeight: 700,
                                                }}>{r}</span>
                                            ))}
                                        </span>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </div>
        </div>
    );
}

// ── Shared match list for group and round-robin phases ────────────────────────

function MatchList({
    matches, tournament, onMatchClick, showGroup = false,
}: {
    matches: TournamentMatchDto[];
    tournament: TournamentDto;
    onMatchClick: (m: TournamentMatchDto) => void;
    showGroup?: boolean;
}) {
    const { t } = useTranslation();

    const statusColor: Record<string, string> = {
        Scheduled: 'blue', Reported: 'orange', Confirmed: 'green',
        Disputed: 'red', Resolved: 'purple', Walkover: 'grey',
    };

    // Group matches by round number
    const byRound = new Map<number, TournamentMatchDto[]>();
    for (let i = 0; i < matches.length; i++) {
        const m = matches[i];
        if (!byRound.has(m.round)) byRound.set(m.round, []);
        byRound.get(m.round)!.push(m);
    }

    const sortedRounds = Array.from(byRound.keys()).sort((a, b) => a - b);
    const totalRounds = sortedRounds.length;

    return (
        <>
            {sortedRounds.map(round => {
                const roundMatches = byRound.get(round) || [];
                return (
                    <div key={round} style={{ marginBottom: 16 }}>
                        <div style={{
                            fontWeight: 700, color: '#888', fontSize: '0.8em',
                            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
                        }}>
                            {round === sortedRounds[totalRounds - 1] && !showGroup
                                ? t('tournament.bracket.final')
                                : `${t('tournament.bracket.round')} ${round}`}
                        </div>
                        {roundMatches.map(m => (
                            <Segment
                                key={m.id}
                                style={{ padding: '10px 14px', cursor: 'pointer', marginBottom: 6 }}
                                onClick={() => onMatchClick(m)}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                                        {showGroup && m.groupNumber && (
                                            <Label size="mini" color="blue" basic>
                                                {t('tournament.groupStage.group')} {String.fromCharCode(64 + m.groupNumber)}
                                            </Label>
                                        )}
                                        <span style={{ fontWeight: 600 }}>{m.teamAName || 'BYE'}</span>
                                        <span style={{ margin: '0 8px', fontWeight: 900, color: m.result ? '#333' : '#ccc' }}>
                                            {m.result ? `${m.result.scoreA} : ${m.result.scoreB}` : 'vs'}
                                        </span>
                                        <span style={{ fontWeight: 600 }}>{m.teamBName || 'TBD'}</span>
                                        {m.scheduledAt && (
                                            <span style={{ fontSize: '0.78em', color: '#999' }}>
                                                <Icon name="calendar" size="small" />
                                                {format(new Date(m.scheduledAt), 'dd MMM, HH:mm')}
                                            </span>
                                        )}
                                    </div>
                                    <Label size="small" color={statusColor[m.status] as any}>
                                        {t(`tournament.matchStatus.${m.status.toLowerCase()}`)}
                                    </Label>
                                </div>
                            </Segment>
                        ))}
                    </div>
                );
            })}
        </>
    );
}

// ── Knockout match list with admin team assignment dropdowns ──────────────────

function KnockoutMatchList({
    matches, tournament, onMatchClick, registrations,
}: {
    matches: TournamentMatchDto[];
    tournament: TournamentDto;
    onMatchClick: (m: TournamentMatchDto) => void;
    registrations: TournamentRegistrationDto[];
}) {
    const { tournamentStore } = useStore();
    const { t } = useTranslation();
    const canAdmin = tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;

    const statusColor: Record<string, string> = {
        Scheduled: 'blue', Reported: 'orange', Confirmed: 'green',
        Disputed: 'red', Resolved: 'purple', Walkover: 'grey',
    };

    // Build dropdown options from registered teams
    const teamOptions = registrations
        .filter(r => r.status === 'Registered')
        .map(r => ({ key: r.teamId, value: r.teamId, text: r.teamName }));

    // Group matches by round
    const byRound = new Map<number, TournamentMatchDto[]>();
    for (const m of matches) {
        if (!byRound.has(m.round)) byRound.set(m.round, []);
        byRound.get(m.round)!.push(m);
    }
    const sortedRounds = Array.from(byRound.keys()).sort((a, b) => a - b);
    const totalRounds = sortedRounds.length;

    return (
        <>
            {sortedRounds.map(round => {
                const roundMatches = byRound.get(round) || [];
                const isFinal = round === sortedRounds[totalRounds - 1];
                return (
                    <div key={round} style={{ marginBottom: 16 }}>
                        <div style={{
                            fontWeight: 700, color: '#888', fontSize: '0.8em',
                            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
                        }}>
                            {isFinal
                                ? t('tournament.bracket.final')
                                : `${t('tournament.bracket.round')} ${round}`}
                        </div>
                        {roundMatches.map(m => (
                            <Segment key={m.id} style={{ marginBottom: 6 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, flexWrap: 'wrap' }}>

                                        {/* Team A — dropdown if TBD and user is admin, otherwise plain text */}
                                        {canAdmin && !m.teamAId ? (
                                            <Dropdown
                                                selection search clearable
                                                options={teamOptions}
                                                placeholder="TBD"
                                                value={m.teamAId ?? ''}
                                                onChange={(_, d) =>
                                                    tournamentStore.setMatchTeams(
                                                        m.id,
                                                        (d.value as string) || null,
                                                        m.teamBId ?? null
                                                    )
                                                }
                                                style={{ minWidth: 140 }}
                                            />
                                        ) : (
                                            <span style={{ fontWeight: 600 }}>{m.teamAName || 'TBD'}</span>
                                        )}

                                        <span style={{ margin: '0 8px', fontWeight: 900, color: m.result ? '#333' : '#ccc' }}>
                                            {m.result ? `${m.result.scoreA} : ${m.result.scoreB}` : 'vs'}
                                        </span>

                                        {/* Team B — dropdown if TBD and user is admin, otherwise plain text */}
                                        {canAdmin && !m.teamBId ? (
                                            <Dropdown
                                                selection search clearable
                                                options={teamOptions}
                                                placeholder="TBD"
                                                value={m.teamBId ?? ''}
                                                onChange={(_, d) =>
                                                    tournamentStore.setMatchTeams(
                                                        m.id,
                                                        m.teamAId ?? null,
                                                        (d.value as string) || null
                                                    )
                                                }
                                                style={{ minWidth: 140 }}
                                            />
                                        ) : (
                                            <span style={{ fontWeight: 600 }}>{m.teamBName || 'TBD'}</span>
                                        )}

                                        {m.scheduledAt && (
                                            <span style={{ fontSize: '0.78em', color: '#999' }}>
                                                <Icon name="calendar" size="small" />
                                                {format(new Date(m.scheduledAt), 'dd MMM, HH:mm')}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <Label size="small" color={statusColor[m.status] as any}>
                                            {t(`tournament.matchStatus.${m.status.toLowerCase()}`)}
                                        </Label>
                                        {/* Only allow opening the match modal when both teams are assigned */}
                                        {m.teamAId && m.teamBId && (
                                            <Button size="mini" basic onClick={() => onMatchClick(m)}>
                                                <Icon name="eye" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </Segment>
                        ))}
                    </div>
                );
            })}
        </>
    );
}

// ── Round Robin main view ─────────────────────────────────────────────────────

const TournamentRoundRobinTable = observer(function TournamentRoundRobinTable() {
    const { tournamentStore } = useStore();
    const { t } = useTranslation();
    const [selectedMatch, setSelectedMatch] = useState<TournamentMatchDto | null>(null);

    const { standings, tournamentMatches, loading, selectedTournament } = tournamentStore;

    if (loading) return <Loader active inline="centered" style={{ margin: 30 }} />;

    if (standings.length === 0) {
        return (
            <Message info>
                <Icon name="info circle" />
                {t('tournament.standings.noData')}
            </Message>
        );
    }

    return (
        <div>
            <StandingsTable rows={standings} />

            <Header as="h4" color="grey" style={{ marginTop: 24 }}>
                <Icon name="list" />{t('tournament.standings.matchList')}
            </Header>
            <MatchList
                matches={tournamentMatches}
                tournament={selectedTournament!}
                onMatchClick={setSelectedMatch}
            />

            {selectedMatch && selectedTournament && (
                <TournamentMatchModal
                    match={selectedMatch}
                    tournament={selectedTournament}
                    onClose={() => setSelectedMatch(null)}
                />
            )}
        </div>
    );
});

export default TournamentRoundRobinTable;

// ── Group Stage + Knockout view ───────────────────────────────────────────────

export const TournamentGroupStageView = observer(function TournamentGroupStageView({
    tournament,
}: { tournament: TournamentDto }) {
    const { tournamentStore } = useStore();
    const { t } = useTranslation();
    const [selectedMatch, setSelectedMatch] = useState<TournamentMatchDto | null>(null);

    const { standings, tournamentMatches, loading } = tournamentStore;
    const canAdmin = tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;

    if (loading) return <Loader active inline="centered" style={{ margin: 30 }} />;

    // Group standing rows by their group number
    const groups = new Map<number, TournamentStandingRow[]>();
    for (let i = 0; i < standings.length; i++) {
        const row = standings[i];
        const g = row.groupNumber ?? 0;
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g)!.push(row);
    }

    // Separate group-phase matches from knockout matches
    const groupMatches: TournamentMatchDto[] = [];
    const knockoutMatches: TournamentMatchDto[] = [];
    for (let i = 0; i < tournamentMatches.length; i++) {
        const match = tournamentMatches[i];
        if (match.groupNumber != null) {
            groupMatches.push(match);
        } else {
            knockoutMatches.push(match);
        }
    }

    const sortedGroups = Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);
    const hasTbdKnockout = knockoutMatches.some(m => !m.teamAId || !m.teamBId);

    return (
        <div>
            {/* Group standings — one table per group */}
            <Header as="h3" color="teal">
                <Icon name="th" />{t('tournament.groupStage.groupPhase')}
            </Header>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {sortedGroups.map(([g, rows]) => (
                    <div key={g} style={{ flex: '1 1 320px', minWidth: 280 }}>
                        <StandingsTable
                            rows={rows}
                            title={`${t('tournament.groupStage.group')} ${String.fromCharCode(64 + g)}`}
                            highlightAdvancing={true}
                            advancingCount={tournament.teamsAdvancingPerGroup}
                        />
                    </div>
                ))}
            </div>

            {/* Group phase match list */}
            {groupMatches.length > 0 && (
                <>
                    <Header as="h4" color="grey" style={{ marginTop: 8 }}>
                        <Icon name="list" />{t('tournament.groupStage.groupMatches')}
                    </Header>
                    <MatchList
                        matches={groupMatches}
                        tournament={tournament}
                        onMatchClick={setSelectedMatch}
                        showGroup={true}
                    />
                </>
            )}

            {/* Knockout phase */}
            {knockoutMatches.length > 0 && (
                <>
                    <Header as="h3" color="orange" style={{ marginTop: 24 }}>
                        <Icon name="sitemap" />{t('tournament.groupStage.knockoutPhase')}
                    </Header>

                    {/* Auto-populate button — only for admins when TBD slots exist */}
                    {canAdmin && hasTbdKnockout && (
                        <Button
                            color="orange"
                            size="small"
                            style={{ marginBottom: 12 }}
                            loading={tournamentStore.submitting}
                            onClick={() => tournamentStore.populateKnockoutFromStandings(tournament.id)}
                        >
                            <Icon name="magic" />
                            {t('tournament.groupStage.autoPopulateKnockout')}
                        </Button>
                    )}

                    <KnockoutMatchList
                        matches={knockoutMatches}
                        tournament={tournament}
                        onMatchClick={setSelectedMatch}
                        registrations={tournament.registrations ?? []}
                    />
                </>
            )}

            {selectedMatch && (
                <TournamentMatchModal
                    match={selectedMatch}
                    tournament={tournament}
                    onClose={() => setSelectedMatch(null)}
                />
            )}
        </div>
    );
});